// Agenda do pipeline de licitações: todos os prazos das licitações vivas num
// calendário só — o prazo da proposta de cada uma e, quando houver, o próximo
// prazo da tabela `prazos` (sessão, recurso, impugnação…). Fase 1 mostra esses
// dois; a Fase 2 (prazos e ocorrências) traz os demais tipos.
import { useMemo, useState } from 'react';
import { Calendar, Card, Flex, Popover, Tag, Tooltip, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { ehAberta, etapaDe } from './etapas';
import { ROTULO_PRAZO, type LicitacaoPipeline } from './saudeLicitacao';
import { classificarPrazoEdital } from '../prazo';

const { Text } = Typography;

type Marca = { l: LicitacaoPipeline; dia: string; tipo: string; rotulo: string; cor: string; negrito: boolean; hora: string };

const COR_TIPO: Record<string, string> = {
  proposta: '#0930D0', sessao: '#722ED1', impugnacao: '#B07500', esclarecimento: '#B07500',
  visita_tecnica: '#0a7f4a', diligencia: '#B91626', recurso_intencao: '#B91626', recurso_razoes: '#B91626',
  contrarrazoes: '#B91626', assinatura: '#0a7f4a', garantia: '#5A6378', outro: '#5A6378',
};

const nomeDoMes = (m: Dayjs) => {
  const t = m.toDate().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return t.charAt(0).toUpperCase() + t.slice(1);
};

export function AgendaLicitacoes({ lista, onAbrir }: { lista: LicitacaoPipeline[]; onAbrir: (l: LicitacaoPipeline) => void }) {
  const [mes, setMes] = useState<Dayjs>(() => dayjs());
  const [ligados, setLigados] = useState<Set<string>>(() => new Set(Object.keys(COR_TIPO)));
  const alternar = (t: string) => setLigados(prev => { const n = new Set(prev); if (n.has(t)) n.delete(t); else n.add(t); return n; });

  const marcas = useMemo(() => {
    const out: Marca[] = [];
    for (const l of lista) {
      if (!ehAberta(l.status_interno)) continue;
      const add = (tipo: string, iso: string | null | undefined) => {
        const c = classificarPrazoEdital(iso);
        if (!c) return;
        out.push({ l, dia: iso!.slice(0, 10), tipo, rotulo: ROTULO_PRAZO[tipo] || tipo, cor: c.vencida ? '#B91626' : (COR_TIPO[tipo] || '#5A6378'),
          negrito: !c.vencida && c.dias <= 3, hora: iso!.slice(11, 16) });
      };
      add('proposta', l.prazo_efetivo);
      if (l.proximo_prazo && l.proximo_prazo !== l.prazo_efetivo) add(l.proximo_prazo_tipo || 'outro', l.proximo_prazo);
    }
    return out;
  }, [lista]);

  const tiposPresentes = useMemo(() => Array.from(new Set(marcas.map(m => m.tipo))), [marcas]);
  const porDia = useMemo(() => {
    const map = new Map<string, Marca[]>();
    for (const m of marcas) {
      if (!ligados.has(m.tipo)) continue;
      if (!map.has(m.dia)) map.set(m.dia, []);
      map.get(m.dia)!.push(m);
    }
    map.forEach(v => v.sort((a, b) => a.hora.localeCompare(b.hora)));
    return map;
  }, [marcas, ligados]);

  const chaveMes = mes.format('YYYY-MM');
  const doMes = marcas.filter(m => m.dia.startsWith(chaveMes));

  const linha = (m: Marca, completo = false) => (
    <Tooltip key={m.tipo + m.l.id} title={completo ? undefined : `${m.rotulo} ${m.hora} — ${m.l.numero_edital || ''} · ${m.l.orgao || ''}`}>
      <div onClick={(e) => { e.stopPropagation(); onAbrir(m.l); }} role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter') onAbrir(m.l); }}
        style={{ cursor: 'pointer', fontSize: completo ? 12 : 10.5, lineHeight: completo ? '18px' : '15px',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#0E1433', fontWeight: m.negrito ? 600 : 400,
          padding: '0 4px 0 6px', borderLeft: `3px solid ${m.cor}`, borderRadius: 2, background: completo ? undefined : `${m.cor}14`,
          marginBottom: 2, maxWidth: completo ? 420 : undefined }}>
        <span style={{ color: '#5A6378' }}>{m.hora} {m.rotulo} · </span>{m.l.numero_edital || m.l.orgao}
        {completo && <span style={{ color: '#5A6378' }}> — {m.l.orgao} · {etapaDe(m.l.status_interno)?.rotulo}</span>}
      </div>
    </Tooltip>
  );

  const cellRender = (date: Dayjs, info: { type: string }) => {
    if (info.type !== 'date') return null;
    const itens = porDia.get(date.format('YYYY-MM-DD')) || [];
    if (!itens.length) return null;
    return (
      <div style={{ overflow: 'hidden' }}>
        {itens.slice(0, 3).map(m => linha(m))}
        {itens.length > 3 && (
          <Popover trigger="click" placement="rightTop" title={date.format('DD/MM/YYYY')}
            content={<div style={{ maxHeight: 320, overflowY: 'auto' }}>{itens.map(m => linha(m, true))}</div>}>
            <button type="button" onClick={e => e.stopPropagation()}
              style={{ border: 0, background: 'none', padding: 0, cursor: 'pointer', fontSize: 10.5, color: '#0930D0' }}>
              +{itens.length - 3} no dia
            </button>
          </Popover>
        )}
      </div>
    );
  };

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={10} style={{ marginBottom: 8 }}>
        <Flex gap={16} wrap="wrap" align="baseline">
          <Text strong style={{ fontSize: 13 }}>{nomeDoMes(mes)}</Text>
          <Text style={{ fontSize: 12, color: '#5A6378' }}>
            <b style={{ color: '#0E1433' }}>{doMes.length}</b> {doMes.length === 1 ? 'prazo' : 'prazos'} no mês
            {' · '}<b style={{ color: '#0E1433' }}>{new Set(doMes.map(m => m.l.id)).size}</b> {new Set(doMes.map(m => m.l.id)).size === 1 ? 'licitação' : 'licitações'}
          </Text>
        </Flex>
        <Flex gap={6} wrap="wrap">
          {tiposPresentes.map(t => (
            <Tag.CheckableTag key={t} checked={ligados.has(t)} onChange={() => alternar(t)}
              style={{ margin: 0, fontSize: 11, border: '1px solid #E3E6EE' }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, marginRight: 5, background: ligados.has(t) ? '#fff' : COR_TIPO[t] || '#5A6378' }} />
              {ROTULO_PRAZO[t] || t}
            </Tag.CheckableTag>
          ))}
        </Flex>
      </Flex>
      <Card size="small" styles={{ body: { padding: 8 } }}>
        <Calendar value={mes} onPanelChange={(v) => setMes(v)} onSelect={(v) => setMes(v)} cellRender={cellRender as any} />
      </Card>
    </div>
  );
}
